import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MAT_TIME_LOCALE, MatTimepicker, MatTimepickerModule, provideNativeDateTimeAdapter } from '@dhutaryan/ngx-mat-timepicker';

@Component({
  selector: 'app-time-input-v4',
  templateUrl: './time-input-v4.component.html',
  styleUrls: ['./time-input-v4.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatTimepickerModule],
  providers: [provideNativeDateTimeAdapter(), { provide: MAT_TIME_LOCALE, useValue: 'en-US' }],
})
export class TimeInputV4Component implements OnChanges {
  @Input() label = 'Time';
  @Input() name = 'time';
  @Input() required = false;
  @Input() timeValue = '';
  @Input() format: 12 | 24 = 24;
  @Input() min: string | null = null; // HH:mm
  @Input() disabled = false;
  @Output() timeValueChange = new EventEmitter<string>();
  @Output() timeChanged = new EventEmitter<string>();

  @ViewChild('picker') timepicker!: MatTimepicker<Date>;

  internalDate: Date | null = null;

  minDate: Date | null = null;
  private minMinutes: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['min']) {
      this.minMinutes = this.parseHHmmToMinutes(this.min);
      this.minDate = this.minMinutes === null ? null : this.minutesToDate(this.minMinutes);
    }

    if (changes['timeValue']) {
      this.internalDate = this.parseTime(this.timeValue);
    }

    this.applyMinClampIfNeeded();
  }

  openTimepicker() {
    this.timepicker.open();
  }

  onTimeChange(value: Date | null) {
    if (!value) {
      this.internalDate = null;
      this.timeValue = '';
      this.timeChanged.emit('');
      return;
    }

    const clamped = this.clampToMin(value);
    this.internalDate = clamped;
    this.emit(this.formatTime(clamped));
  }

  onTimeSet(value: string | Event): void {
    const str =
      typeof value === 'string'
        ? value
        : (value.target as HTMLInputElement | null)?.value ?? '';

    const parsed = this.parseTime(str);
    const clamped = parsed ? this.clampToMin(parsed) : null;

    if (!clamped) {
      this.clearTimeValue();
      return;
    }

    this.internalDate = clamped;
    this.emit(this.formatTime(clamped));
  }

  clearTimeValue(): void {
    this.internalDate = null;
    this.timeValue = '';
    this.timeValueChange.emit('');
    this.timeChanged.emit('');
  }

  private emit(value: string): void {
    this.timeValue = value;
    this.timeValueChange.emit(value);
    this.timeChanged.emit(value);
  }

  private applyMinClampIfNeeded(): void {
    if (!this.internalDate) return;
    if (this.minMinutes === null) return;

    const current = this.toMinutes(this.internalDate);
    if (current < this.minMinutes) {
      const clamped = this.minutesToDate(this.minMinutes);
      this.internalDate = clamped;
      this.emit(this.formatTime(clamped));
    }
  }

  private clampToMin(date: Date): Date {
    if (this.minMinutes === null) return date;
    return this.toMinutes(date) < this.minMinutes ? this.minutesToDate(this.minMinutes) : date;
  }

  private parseTime(value: string): Date | null {
    const mins = this.parseHHmmToMinutes(value);
    return mins === null ? null : this.minutesToDate(mins);
  }

  private parseHHmmToMinutes(value: string | null | undefined): number | null {
    if (!value) return null;
    const parts = value.split(':');
    if (parts.length !== 2) return null;

    const h = Number(parts[0]);
    const m = Number(parts[1]);

    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;

    return h * 60 + m;
  }

  private toMinutes(date: Date): number {
    return date.getHours() * 60 + date.getMinutes();
  }

  private minutesToDate(totalMinutes: number): Date {
    const d = new Date();
    d.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
    return d;
  }

  private formatTime(date: Date): string {
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }
}